import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { styles, Text } from '@frontend/design-system';
import type { CalendarEvent, ExceptionV2CalendarEvent, ExceptionV3CalendarEvent } from '../../../types';
import { stripeBackgroundStyle } from '../../styles';

interface Props<T> {
  event: T;
}

const STICKY_EXCEPTION_DURATION_THRESHOLD = 120;
const COMPACT_EXCEPTION_DURATION_THRESHOLD = 20;

export const isStickyExceptionV3 = (event: CalendarEvent): boolean =>
  event.eventType === 'exceptionV3' && (event.duration ?? 0) > STICKY_EXCEPTION_DURATION_THRESHOLD;

export const ExceptionV3Event = ({ event }: Props<ExceptionV3CalendarEvent>) => {
  const isCompact = (event.duration ?? 0) < COMPACT_EXCEPTION_DURATION_THRESHOLD;
  const isStickyText = isStickyExceptionV3(event);

  return (
    <div data-event-type={event.eventType} css={[containerStyle, !isStickyText && styles.flexCenter]}>
      <Text
        as='span'
        weight='bold'
        size='small'
        color='subdued'
        css={[textStyle, isCompact && compactTextStyle, isStickyText && stickyTextStyle]}
      >
        {event.title}
      </Text>
    </div>
  );
};

// we were overriding exception with custom card so this is not actually get render currently
export const ExceptionV2Event = ({ event }: Props<ExceptionV2CalendarEvent>) => {
  return (
    <div data-event-type={event.eventType} css={[stripeBackgroundStyle, containerStyle]}>
      {event.eventType}
    </div>
  );
};

const containerStyle = css({
  background: theme.colors.neutral10,
  borderRadius: theme.borderRadius.small,
  color: theme.colors.neutral60,
  fontWeight: theme.font.weight.bold,
  fontSize: theme.font.size.small,
  border: `1px dashed ${theme.colors.neutral20}`,
  height: 'stretch',
  '> span': {
    background: `color-mix(in srgb, transparent 60%, ${theme.colors.white})`,
  },
  ':hover': {
    background: theme.colors.neutral20,
    '> span': {
      background: `color-mix(in srgb, transparent 40%, ${theme.colors.white})`,
    },
  },
});

const textStyle = css({
  margin: theme.spacing(0, 1.5),
  padding: theme.spacing(0, 0.5),
  width: 'max-content',
  maxWidth: 'calc(100% - 24px)',
  marginInline: 'auto',
  borderRadius: theme.borderRadius.small,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  minWidth: 0,
});

const compactTextStyle = css({
  display: 'block',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});
const stickyTextStyle = css({
  position: 'sticky',
  top: 'calc(var(--viewport-height) / 2)',
  transform: 'translateY(calc(-50% + 20px))',
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(5),
});
